import { CommonModule, CurrencyPipe } from '@angular/common';
import { AfterViewInit, Component, EventEmitter, Output, ViewChild } from '@angular/core';
import { MatTableDataSource, MatTableModule } from '@angular/material/table';
import { FormsModule } from '@angular/forms';
import { MatSort, MatSortModule } from '@angular/material/sort';
import { MatPaginator, MatPaginatorIntl, MatPaginatorModule } from '@angular/material/paginator';
import { CommonFunctionsService } from '../../../services/common-functions.service';
import { CommonDataService } from '../../../services/common-data.service';
import { Subject, takeUntil } from 'rxjs';
import { ReviewService } from '../../../services/review.service';
import { Constants } from '../../../constants/constants';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { CustomMatPaginatorIntl } from '../../../services/custom-mat-paginator.service';
import { ExportObjectAsFieldValuePDFOptions } from '../../../models/ExportObjectAsFieldValuePDFOptions';
import { ExportTablesToPDFOptions } from '../../../models/ExportTableToPDF';
import { CostByPartNumber } from '../../../models/CostByPartNumber';
import { DecimalPrecisionPipe } from '../../../pipes/decimal-precision/decimal-precision.pipe';

@Component({
  selector: 'app-sol-material',
  imports: [CommonModule, MatTableModule, FormsModule, MatSortModule, MatPaginatorModule, TranslatePipe],
  providers: [
    { provide: MatPaginatorIntl, useClass: CustomMatPaginatorIntl },
    DecimalPrecisionPipe
  ],
  templateUrl: './sol-material.component.html',
  styleUrl: './sol-material.component.scss'
})
export class SolMaterialComponent implements AfterViewInit {

  materialDataList: CostByPartNumber[] = new Array<CostByPartNumber>();
  dataSourceMaterialWise = new MatTableDataSource<CostByPartNumber>(this.materialDataList);
  @ViewChild(MatPaginator) Levelpaginator: MatPaginator | null = null;
  @ViewChild(MatSort) sort!: MatSort;
  @Output() disablePdf = new EventEmitter<boolean>();
  costByPartNumberDetails: CostByPartNumber[] = new Array<CostByPartNumber>();
  checkDataExists: boolean = false;
  destroy$ = new Subject<void>();
  pageSizeLevelWise: number = 17;

  columns: { field: string, header: string, isRightAlign: boolean }[] = [
    { field: 'referenceNumber', header: 'REVIEW_SOL_MATERIAL_TAB.REFERENCE_NUMBER', isRightAlign: false },
    { field: 'description', header: 'REVIEW_SOL_MATERIAL_TAB.DESCRIPTION', isRightAlign: false },
    { field: 'quantity', header: 'REVIEW_SOL_MATERIAL_TAB.QUANTITY', isRightAlign: true },
    { field: 'unitCost', header: 'REVIEW_SOL_MATERIAL_TAB.UNIT_COST', isRightAlign: true },
    { field: 'cost', header: 'REVIEW_SOL_MATERIAL_TAB.COST', isRightAlign: true },
  ];
  displayedColumnsMaterialWise: string[] = [
    'referenceNumber',
    'description',
    'quantity',
    'unitCost',
    'cost',
  ];
  headersMaterialWise: { [key: string]: string } = {};

  constructor(public commonFunctions: CommonFunctionsService, 
    private readonly commonDataService: CommonDataService, 
    private readonly reviewService: ReviewService, 
    private readonly translate: TranslateService, 
    private readonly currencyPipe: CurrencyPipe,
    private readonly decimalPrecisionPipe: DecimalPrecisionPipe) { }

  ngOnInit() {
    this.initializeMaterialWise();
    this.commonDataService.triggerTabMethod$
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        this.exportToPdf(Constants.SOL_MATERIAL);
      });
    this.sendDisablePdfEvent();
  }

  sendDisablePdfEvent() {
    this.disablePdf.emit(false);
  }

  ngAfterViewInit() {
    this.dataSourceMaterialWise.paginator = this.Levelpaginator;
    if (this.Levelpaginator) {
      this.Levelpaginator.pageSize = this.pageSizeLevelWise;
    }
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  initializeMaterialWise() {
    const newCostReviewObject = this.reviewService.newCostReviewObject;
    if (newCostReviewObject?.costByPartNumberDetails) {
      this.costByPartNumberDetails = newCostReviewObject.costByPartNumberDetails;
      if (this.costByPartNumberDetails.length > 0) {
        this.checkDataExists = true;
        this.getAllMappedMaterialWise();
      }
    }
  }

  translateHeaders() {
    this.headersMaterialWise = {
      section: this.translate.instant('REVIEW_SOL_MATERIAL_TAB.SECTION'),
      referenceNumber: this.translate.instant('REVIEW_SOL_MATERIAL_TAB.REFERENCE_NUMBER'),
      description: this.translate.instant('REVIEW_SOL_MATERIAL_TAB.DESCRIPTION'),
      quantity: this.translate.instant('REVIEW_SOL_MATERIAL_TAB.QUANTITY'),
      unitCost: this.translate.instant('REVIEW_SOL_MATERIAL_TAB.UNIT_COST'),
      cost: this.translate.instant('REVIEW_SOL_MATERIAL_TAB.COST'),
      pmAdjustedHours: this.translate.instant('REVIEW_SOL_MATERIAL_TAB.PM_ADJUSTED_HOURS'),
      engAdjustedHours: this.translate.instant('REVIEW_SOL_MATERIAL_TAB.ENG_ADJUSTED_HOURS'),
      specAdjustedHours: this.translate.instant('REVIEW_SOL_MATERIAL_TAB.SPEC_ADJUSTED_HOURS'),
      mechAdjustedHours: this.translate.instant('REVIEW_SOL_MATERIAL_TAB.MECH_ADJUSTED_HOURS'),
      elecAdjustedHours: this.translate.instant('REVIEW_SOL_MATERIAL_TAB.ELEC_ADJUSTED_HOURS'),
    };
  }
  getAllMappedMaterialWise() {
    this.materialDataList = this.costByPartNumberDetails
      .filter((detail: CostByPartNumber) => detail.section !== Constants.FILTER_DATA && detail.section !== Constants.EMPTY_STRING)
      .map((detail: CostByPartNumber) => ({
        ...detail,
        unitCost: this.decimalPrecisionPipe.transform(detail.unitCost),
        cost: this.decimalPrecisionPipe.transform(detail.cost),
      }));
    this.dataSourceMaterialWise.data = this.materialDataList;
  }


  exportToPdf(tabName: string) {
    if (tabName != Constants.SOL_MATERIAL) {
      return;
    }
    this.translateHeaders();
    if (!this.materialDataList || this.materialDataList.length === Constants.ZERO) {
      const options: ExportObjectAsFieldValuePDFOptions = {
        data: { [this.translate.instant('REVIEW_SOL_MATERIAL_TAB.MATERIAL_DATA_NOT_FOUND')]: Constants.EMPTY_STRING },
        title: Constants.EMPTY_STRING,
        fileName: 'materials.pdf',
        estimateID: this.reviewService.selectedReview.estimateID,
        estimateName: this.reviewService.selectedReview.request,
        tabName: this.translate.instant('REVIEW_SOL_TABS_LABELS.MATERIAL'),
      };
      this.commonFunctions.exportObjectAsFieldValuePDF(options);
      return;
    }

    const options: ExportTablesToPDFOptions = {
      tables: [{
        title: 'Materials Report',
        displayedColumns: this.displayedColumnsMaterialWise,
        headers: this.headersMaterialWise,
        data: this.materialDataList,
        typeGuard: (row, col) => row[col as keyof typeof row]
      }],
      fileName: 'materials.pdf',
      hasNotes: false,
      estimateID: this.reviewService.selectedReview.estimateID,
      estimateName: this.reviewService.selectedReview.request,
      tabName: this.translate.instant('REVIEW_SOL_TABS_LABELS.MATERIAL'),
    };
    this.commonFunctions.exportTablesToPDF(options);
  }

  pageChangeTrigger(materialPageSizeInput: HTMLInputElement, event: Event) {
    this.reviewService.onPageSizeLevelWiseChange(materialPageSizeInput.value, this.Levelpaginator, event);
  }
}
